import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PlayProps } from "@/types/puzzle";

type Pair = { word: string; translation: string };
type TileState = "default" | "selected" | "matched" | "wrong";

// How long a mismatched pair stays red before it resets for another try.
const WRONG_FLASH_MS = 700;

export default function MatchPairsPlay({ data, onAnswer, result }: PlayProps) {
  const { t } = useTranslation();
  const words = (data.words as string[]) ?? [];
  const translations = (data.translations as string[]) ?? [];
  const pairs = (data.pairs as Pair[]) ?? [];

  const answered = result !== null;

  // word -> its correct translation. Words and translations are validated unique
  // per exercise, so matching the original tiles is exact and unambiguous — no
  // normalization needed (the student clicks given tiles, never types).
  const correctFor = useMemo(() => new Map(pairs.map((p) => [p.word, p.translation])), [pairs]);

  const [matched, setMatched] = useState<Set<string>>(new Set());
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [selectedTranslation, setSelectedTranslation] = useState<string | null>(null);
  const [wrong, setWrong] = useState<Pair | null>(null);

  // A ref, not state: the count is never rendered, and reading it at submit time
  // avoids any stale-closure risk (a render-time `mistakes` value could lag).
  const mistakesRef = useRef(0);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submittedRef = useRef(false);

  const matchedTranslations = useMemo(
    () => new Set([...matched].map((w) => correctFor.get(w))),
    [matched, correctFor],
  );
  const flashing = wrong !== null; // ignore clicks while a wrong pair is flashing

  // One tile from each column is now chosen: lock both green if correct, else
  // flash both red, count a mistake, and clear so the student picks again.
  const resolve = (word: string, translation: string) => {
    if (correctFor.get(word) === translation) {
      const next = new Set(matched).add(word);
      setMatched(next);
      setSelectedWord(null);
      setSelectedTranslation(null);
      // No Check button — auto-submit the instant the board is complete. Every
      // pair is correct by construction here; `mistakes` decides pass/fail on the
      // server. Submitting inside the click keeps the success chime within the
      // user gesture (autoplay policy).
      if (next.size === words.length && !submittedRef.current) {
        submittedRef.current = true;
        onAnswer({ pairs, mistakes: mistakesRef.current });
      }
    } else {
      setWrong({ word, translation });
      mistakesRef.current += 1;
      flashTimer.current = setTimeout(() => {
        setWrong(null);
        setSelectedWord(null);
        setSelectedTranslation(null);
      }, WRONG_FLASH_MS);
    }
  };

  const pickWord = (word: string) => {
    if (answered || flashing || matched.has(word)) return;
    if (selectedTranslation) resolve(word, selectedTranslation);
    else setSelectedWord((cur) => (cur === word ? null : word));
  };

  const pickTranslation = (translation: string) => {
    if (answered || flashing || matchedTranslations.has(translation)) return;
    if (selectedWord) resolve(selectedWord, translation);
    else setSelectedTranslation((cur) => (cur === translation ? null : translation));
  };

  // Clear a pending flash timer on unmount.
  useEffect(() => () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
  }, []);

  const tileClasses: Record<TileState, string> = {
    matched: "bg-green-500/15 text-green-700 border-green-500/40",
    wrong: "bg-red-500/15 text-red-700 border-red-500/40 animate-shake",
    selected: "bg-foreground text-background border-foreground",
    default: "bg-secondary text-foreground border-border hover:bg-secondary/80",
  };

  const wordState = (word: string): TileState =>
    matched.has(word) ? "matched" : wrong?.word === word ? "wrong" : selectedWord === word ? "selected" : "default";
  const translationState = (tr: string): TileState =>
    matchedTranslations.has(tr) ? "matched" : wrong?.translation === tr ? "wrong" : selectedTranslation === tr ? "selected" : "default";

  // Fixed height per tile so the grid never reflows as pairs get matched.
  const renderTile = (label: string, state: TileState, onClick: () => void) => (
    <button
      key={label}
      onClick={onClick}
      disabled={answered || state === "matched"}
      className={`flex h-14 w-full items-center justify-center overflow-hidden rounded-lg border-2 border-b-4 px-3 text-center text-sm font-bold transition-all active:scale-[0.97] enabled:hover:-translate-y-0.5 disabled:cursor-default ${tileClasses[state]}`}
    >
      <span className="truncate">{label}</span>
    </button>
  );

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      <h2 className="text-lg font-medium text-muted-foreground text-center animate-fade-in-up">
        {t("puzzleStudent.matchPairs.title")}
      </h2>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          {words.map((word) => renderTile(word, wordState(word), () => pickWord(word)))}
        </div>
        <div className="space-y-2">
          {translations.map((tr) => renderTile(tr, translationState(tr), () => pickTranslation(tr)))}
        </div>
      </div>
    </div>
  );
}
